import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import Card from '../components/Card';
import { formatPrice, formatChange } from '../utils/format';
import { useEnhancedCounter, getFlashClass } from '../hooks/useEnhancedCounter';
import { SkeletonBox } from '../components/Skeleton/Skeleton';
import '../App.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LRST_API_URL =
  'https://api.dexscreener.com/latest/dex/tokens/0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45';

// Seeded random function for deterministic sparkline
const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const buildSparkline = (basePrice, change) => {
  const points = 18;
  const base = basePrice > 0 ? basePrice : 1;
  const trend = Math.max(-20, Math.min(20, change || 0)) / 100;
  
  // Use price as seed for deterministic random
  const seed = Math.floor(base * 1000000);
  
  const data = [];
  for (let i = 0; i < points; i++) {
    const p = i / (points - 1);
    const directional = base * (1 + trend * (p - 0.5));
    // Use seeded random instead of Math.random()
    const noise = base * 0.01 * (seededRandom(seed + i) - 0.5);
    data.push(Math.max(0, directional + noise));
  }
  return data;
};

const Dashboard = () => {
  const [lrstData, setLrstData] = useState({ price: 0, change: 0 });
  const [chartData, setChartData] = useState(null);
  const [currentNotice, setCurrentNotice] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  
  // Use enhanced counter with flash effect
  const { value: displayPrice, flashColor: priceFlashColor } = useEnhancedCounter(
    lrstData.price, 
    animationComplete
  );
  const { value: displayChange, flashColor: changeFlashColor } = useEnhancedCounter(
    lrstData.change, 
    animationComplete
  );
  
  const chartContainerRef = useRef(null);
  const location = useLocation();

  const notices = [
    {
      tag: 'Update',
      date: 'Today',
      title: 'LRST Staking is Now Live! Start earning competitive APR rewards by staking your tokens today.',
      action: { label: 'Open Staking', href: '/staking' }
    },
    {
      tag: 'Security',
      date: 'Always',
      title: 'Use official links only. Beware of fake contracts & fake group.',
      action: { label: 'Info', href: 'https://t.me/littlerabbitchat', external: true }
    },
    {
      tag: 'Swap',
      date: 'Reminder',
      title: 'LRST trading is now officially live.',
      action: { label: 'Open Swap', href: '/swap' }
    }
  ];

  // Use sessionStorage to track if dashboard was ever visited AND chart animated
  const [isFirstLoad] = useState(() => {
    return !sessionStorage.getItem('dashboardVisited');
  });
  
  const [hasChartAnimated] = useState(() => {
    return sessionStorage.getItem('chartAnimated') === 'true';
  });

  // Mark dashboard as visited on mount
  useEffect(() => {
    if (isFirstLoad) {
      sessionStorage.setItem('dashboardVisited', 'true');
    }
  }, [isFirstLoad]);

  const roadmapItems = [
    { phase: 'Phase 1 — Launch LRST', status: 'Completed', progress: 100, done: true },
    { phase: 'Phase 2 — Dashboard + Swap', status: 'Completed', progress: 100, done: true },
    { phase: 'Phase 3 — Staking', status: 'Completed', progress: 100, done: true },
    { phase: 'Phase 4 — CEX Listing', status: 'In progress', progress: 45, done: false },
    { phase: 'Phase 5 — Partnership', status: 'In progress', progress: 30, done: false },
    { phase: 'Phase 6 — Launch SaaS', status: 'In progress', progress: 20, done: false },
    { phase: 'Phase 7 — CEX Listing', status: 'In progress', progress: 10, done: false }
  ];

  // Wait for animation to complete before starting real-time updates
  useEffect(() => {
    // If chart has already animated before, skip animation delay
    if (hasChartAnimated) {
      setAnimationComplete(true);
      return;
    }
    
    // Animation duration: 0.7s + delay 0.5s for chart = ~1.2s total
    const timer = setTimeout(() => {
      setAnimationComplete(true);
      // Mark chart as animated so it won't animate again
      sessionStorage.setItem('chartAnimated', 'true');
    }, 1200); // Wait 1.2s to be safe

    return () => clearTimeout(timer);
  }, [hasChartAnimated]);

  useEffect(() => {
    // Only start fetching data after animation is complete
    if (!animationComplete) return;

    const fetchLRSTData = async () => {
      try {
        const res = await fetch(LRST_API_URL, { cache: 'no-store' });
        if (!res.ok) throw new Error('HTTP');
        const data = await res.json();
        const pair = data?.pairs?.[0];
        const price = pair?.priceUsd ? parseFloat(pair.priceUsd) : 0;
        const change =
          pair?.priceChange?.h24 != null
            ? parseFloat(pair.priceChange.h24)
            : pair?.priceChange24h != null
            ? parseFloat(pair.priceChange24h)
            : 0;
        setLrstData({ price, change });

        const sparklineData = buildSparkline(price, change);
        setChartData({
          labels: sparklineData.map(() => ''),
          datasets: [
            {
              data: sparklineData,
              borderColor: 'rgba(255, 255, 255, 0.85)',
              backgroundColor: 'rgba(229, 9, 20, 0.18)',
              fill: true,
              tension: 0.45,
              borderWidth: 2,
              pointRadius: 0
            }
          ]
        });
      } catch (e) {
        console.error('Error fetching LRST data:', e);
      }
    };

    fetchLRSTData();
    const interval = setInterval(fetchLRSTData, 10000);
    return () => clearInterval(interval);
  }, [animationComplete]); // Depend on animationComplete

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentNotice((prev) => (prev + 1) % notices.length);
    }, 6500);
    return () => clearInterval(timer);
  }, [notices.length]);

  // Chart options - with left-to-right line animation (only on first load)
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    // Enable scroll animation ONLY on first load (!hasChartAnimated)
    animation: (!hasChartAnimated && animationComplete) ? {
      duration: 1500,
      easing: 'easeInOutQuart',
      onComplete: () => {
        // After first animation, disable for subsequent updates
      }
    } : false,
    animations: (!hasChartAnimated && animationComplete) ? {
      x: {
        type: 'number',
        duration: 1500,
        from: 0,
        easing: 'easeInOutQuart'
      },
      y: {
        type: 'number', 
        duration: 1500,
        from: 0,
        easing: 'easeInOutQuart'
      }
    } : false,
    responsiveAnimationDuration: 0,
    transitions: {
      active: { animation: { duration: 0 } },
      resize: { animation: { duration: 0 } }
    },
    plugins: { 
      legend: { display: false }, 
      tooltip: { 
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(229, 9, 20, 0.6)',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => `$${context.parsed.y.toFixed(10)}`,
        },
        animation: false
      } 
    },
    scales: { 
      x: { 
        display: false, 
        grid: { display: false }
      }, 
      y: { 
        display: false, 
        grid: { display: false }
      } 
    },
    elements: {
      line: {
        tension: 0.45,
        borderWidth: 2,
      },
      point: {
        radius: 0,
        hitRadius: 10,
        hoverRadius: 5,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    }
  }), [animationComplete, hasChartAnimated]);

  // Memoize chart data to prevent re-renders
  const memoizedChartData = useMemo(() => chartData, [chartData]);

  return (
    <div className="page-container">
      <section>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Card>
            <div className="db-card-title">Little Rabbit Ecosystem.</div>
            <p className="db-card-desc">
              <span className="token-lrst">Little Rabbit Staking Token (LRST)</span> Built with a
              Staking as a Service (SaaS) concept, LRST provides a simple, scalable, and efficient
              solution so developers can focus on growing their projects while offering sustainable
              rewards to holders.
            </p>
          </Card>
        </motion.div>
      </section>

      <section className="db-grid" style={{ marginTop: '16px' }}>
        <motion.article 
          className="db-token"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="db-token-top">
            <div className="db-token-id">
              <img className="db-token-icon" src="/assets/logo-chart-new.png" alt="LRST" />
              <div>
                <div className="db-token-symbol">LRST</div>
                <div className="db-token-name">Little Rabbit Staking Token</div>
              </div>
            </div>
            <div className="db-token-tag">Utility &amp; Staking</div>
          </div>

          <div className="db-addr">
            <span className="db-addr-text">
              0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45
            </span>
            <div className="db-addr-actions">
              <button
                className="db-mini-btn"
                type="button"
                data-press
                onClick={() =>
                  navigator.clipboard.writeText(
                    '0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45'
                  )
                }
              >
                Copy
              </button>
              <button
                className="db-mini-btn"
                type="button"
                data-press
                onClick={() =>
                  window.open(
                    'https://bscscan.com/token/0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45',
                    '_blank'
                  )
                }
              >
                BscScan
              </button>
            </div>
          </div>

          <div className="db-metrics">
            <div className="db-metric">
              <div className="db-mlabel">Price</div>
              <div className={`db-mvalue ${getFlashClass(priceFlashColor)}`}>
                {animationComplete 
                  ? formatPrice(displayPrice) 
                  : '$0.0000000000'}
              </div>
            </div>
            <div className="db-metric">
              <div className="db-mlabel">24h</div>
              <div
                className={`db-mvalue db-change ${getFlashClass(changeFlashColor)} ${
                  lrstData.change > 0.1 ? 'up' : lrstData.change < -0.1 ? 'down' : ''
                }`}
              >
                {animationComplete 
                  ? formatChange(displayChange)
                  : '0.00%'}
              </div>
            </div>
          </div>

          {/* Chart with special animation - line draws from left to right */}
          <motion.div 
            ref={chartContainerRef}
            className="db-chart"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {memoizedChartData ? (
              <Line 
                key={animationComplete ? 'animated' : 'static'} 
                data={memoizedChartData} 
                options={chartOptions} 
              />
            ) : (
              <div style={{ padding: '20px' }}>
                <SkeletonBox width="100%" height="180px" />
              </div>
            )}
          </motion.div>
        </motion.article>
      </section>

      <motion.section 
        className="db-roadmap-section"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <h2 className="db-section-title">Roadmap</h2>
        <div className="db-roadmap-list">
          {roadmapItems.map((item, index) => (
            <div key={index}>
              <div className="db-roadmap-item">
                <div className="db-roadmap-left">
                  <div className="db-roadmap-check">
                    {item.done ? '✓' : '→'}
                  </div>
                  <div className="db-roadmap-text">
                    <div className="db-roadmap-title">{item.phase}</div>
                    <div className="db-roadmap-sub">{item.status}</div>
                  </div>
                </div>
                <div className={`db-roadmap-pill ${item.done ? 'done' : ''}`}>
                  {item.done ? 'Done' : item.status === 'In progress' ? 'In Progress' : 'Next'}
                </div>
              </div>
              <div className="db-roadmap-bar" style={{ marginBottom: index === roadmapItems.length - 1 ? 0 : '8px' }}>
                <div
                  className="db-roadmap-bar-fill"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section 
        className="db-roadmap-section" 
        style={{ marginTop: '18px' }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <h2 className="db-section-title">Community Notice</h2>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Card>
            <div className="db-notice-slider" style={{ overflow: 'hidden' }}>
              <article
                key={currentNotice}
                className="db-notice-slide"
              >
                <div className="db-notice-top">
                  <span className="db-notice-tag">{notices[currentNotice].tag}</span>
                  <span className="db-notice-date">{notices[currentNotice].date}</span>
                </div>
                <div className="db-notice-title">{notices[currentNotice].title}</div>
                <div className="db-notice-actions">
                  <a
                    className={`db-btn ${
                      notices[currentNotice].action.external ? 'db-btn-ghost' : ''
                    }`}
                    href={notices[currentNotice].action.href}
                    target={notices[currentNotice].action.external ? '_blank' : undefined}
                    rel={notices[currentNotice].action.external ? 'noopener noreferrer' : undefined}
                    data-press
                  >
                    {notices[currentNotice].action.label}
                  </a>
                </div>
              </article>
            </div>
          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <button
              className="db-mini-btn"
              type="button"
              onClick={() =>
                setCurrentNotice((prev) => (prev - 1 + notices.length) % notices.length)
              }
              data-press
            >
              ‹
            </button>
            <div style={{ display: 'flex', gap: '6px' }}>
              {notices.map((_, index) => (
                <button
                  key={index}
                  className={`db-dot-btn ${index === currentNotice ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => setCurrentNotice(index)}
                />
              ))}
            </div>
            <button
              className="db-mini-btn"
              type="button"
              onClick={() => setCurrentNotice((prev) => (prev + 1) % notices.length)}
              data-press
            >
              ›
            </button>
          </div>
        </Card>
      </motion.div>
      </motion.section>
    </div>
  );
};

export default Dashboard;
